import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Compass, Globe2, Loader2, MapPin, Search, Sparkles } from "lucide-react";
import api from "../lib/api";
import { chipTint } from "../lib/chipTints";
import { placePhotoQuery, HoverPhotoPreview } from "../components/UnsplashGallery";
import { geoSlug } from "./ExploreGuide";
import TrendingPlaces from "../components/TrendingPlaces";

/* Explore Global Places — cascading Country → State/Region → City/Town/Village picker.
   Every selection opens the /explore guide page; if the guide doesn't exist yet the
   Geo Content Engine generates it in the background automatically. */

const sel = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-blue-600 disabled:bg-zinc-50 disabled:text-zinc-400";

/* remembers the visitor's country/region pick across navigation (until they change it).
   Read FRESH on every mount — a stale module-level snapshot was wiping the pick (and the
   city list) when users returned via the browser back button. */
const readSavedPicker = () => {
  try { return JSON.parse(localStorage.getItem("explore-places-picker") || "{}"); } catch { return {}; }
};
const lbl = "block text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1";

/* Destination chip with a delayed Unsplash photo preview on hover (desktop).
   Photos fetch lazily on first hover only — cached server-side + in-memory. */
function PopularChip({ place: p, tint, onOpen }) {
  return (
    <HoverPhotoPreview query={placePhotoQuery(p.label, p.country)} name={p.label} sub={p.country}
      testId={`popular-preview-${p.slug.split("/").pop()}`}>
      <button onClick={onOpen}
        data-testid={`popular-${p.slug.split("/").pop()}`}
        title={`${p.type} · ${p.country}`}
        style={{ background: tint }}
        className="px-2.5 py-1.5 border border-zinc-200 text-xs font-700 text-zinc-800 hover:border-[#1a73e8] hover:text-[#1a73e8] transition-colors">
        {p.label}
        <span className="ml-1.5 text-[9px] font-800 uppercase text-zinc-500">{p.type === "City" ? "" : p.type}</span>
      </button>
    </HoverPhotoPreview>
  );
}

/* Type-ahead country picker — type a few characters instead of scrolling a 200+ item list. */
function CountryCombobox({ countries, value, onChange }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const norm = q.trim().toLowerCase();
  const filtered = norm
    ? countries.filter((c) => c.name.toLowerCase().includes(norm)).slice(0, 60)
    : countries;
  const pick = (name) => { onChange(name); setOpen(false); setQ(""); };
  return (
    <div className="relative">
      <input
        value={open ? q : value}
        placeholder={value || "Type a country…"}
        onFocus={() => { setOpen(true); setQ(""); }}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && filtered.length > 0) pick(filtered[0].name);
          if (e.key === "Escape") { setOpen(false); setQ(""); e.target.blur(); }
        }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        className={sel} data-testid="explore-country-select" />
      <ChevronDown className="w-4 h-4 absolute right-3 top-3.5 text-zinc-400 pointer-events-none" />
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-auto bg-white border border-zinc-300 shadow-lg"
          data-testid="explore-country-options">
          {filtered.map((c) => (
            <button key={c.slug} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(c.name)}
              data-testid={`explore-country-opt-${c.slug}`}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${c.name === value ? "bg-blue-50 font-700 text-[#1a73e8]" : "text-zinc-800"}`}>
              {c.name}
            </button>
          ))}
          {filtered.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No matching country</div>}
        </div>
      )}
    </div>
  );
}

export default function ExplorePlaces() {
  const nav = useNavigate();
  const [countries, setCountries] = useState(null);
  const [states, setStates] = useState(null);
  const [cities, setCities] = useState(null);
  const [country, setCountry] = useState(() => readSavedPicker().country || "");
  const [state, setState] = useState(() => readSavedPicker().state || "");
  const [cityQ, setCityQ] = useState("");
  const [loadingCities, setLoadingCities] = useState(false);
  const [custom, setCustom] = useState("");
  const [popular, setPopular] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    api.get("/geo/popular").then((r) => setPopular(r.data.items || [])).catch(() => setPopular([]));
  }, []);

  useEffect(() => {
    document.title = "Explore Global Places — Country, State, City & Village Guides | TravelerPulse.pro";
    api.get("/geo/countries").then((r) => setCountries(r.data.items || [])).catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    try { localStorage.setItem("explore-places-picker", JSON.stringify({ country, state })); } catch { /* private mode */ }
  }, [country, state]);

  useEffect(() => {
    setStates(null); setCities(null);
    if (!country) return;
    api.get("/geo/states", { params: { country } }).then((r) => setStates(r.data.items || [])).catch(() => setStates([]));
  }, [country]);

  useEffect(() => {
    setCities(null);
    if (!country) return;
    setLoadingCities(true);
    const t = setTimeout(() => {
      api.get("/geo/cities", { params: { country, ...(state ? { state } : {}), ...(cityQ.trim() ? { q: cityQ.trim() } : {}), limit: 150 } })
        .then((r) => setCities(r.data.items || [])).catch(() => setCities([]))
        .finally(() => setLoadingCities(false));
    }, cityQ ? 400 : 0);
    return () => clearTimeout(t);
  }, [country, state, cityQ]);

  const openSlug = (slug) => slug && nav(`/explore/${slug}`);
  const openCustom = () => {
    if (!custom.trim() || !country) return;
    const parts = [geoSlug(country), state ? geoSlug(state) : null, geoSlug(custom)].filter(Boolean);
    nav(`/explore/${parts.join("/")}`);
  };

  const countryDoc = (countries || []).find((c) => c.name === country);
  const stateDoc = (states || []).find((s) => s.name === state);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="explore-places-page">
      <div className="bg-[#1a73e8] text-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-12">
          <h1 className="font-heading font-900 text-4xl sm:text-5xl flex items-center gap-3">
            <Globe2 className="w-10 h-10 text-[#FBBC05]" /> Explore Global Places
          </h1>
          <p className="text-blue-100 mt-3 text-base md:text-lg max-w-2xl">
            Pick any country, state or district, then any city, town or village on Earth — get a full
            travel guide with news, events, photos and community tips. Never been written before?
            Our engine creates it for you on the spot.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10 space-y-8">
        <TrendingPlaces />
        <div className="bg-white border border-zinc-300 p-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className={lbl}>Country</label>
              {countries === null ? <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div> : (
                <CountryCombobox countries={countries} value={country}
                  onChange={(name) => { setCountry(name); setState(""); setCityQ(""); }} />
              )}
            </div>
            <div>
              <label className={lbl}>State / Province / District</label>
              <select value={state} onChange={(e) => setState(e.target.value)} className={sel} disabled={!country || states === null}
                data-testid="explore-state-select">
                <option value="">{states === null && country ? "Loading…" : "All regions (optional)"}</option>
                {(states || []).map((s) => <option key={s.slug} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Find a city / town / village</label>
              <div className="relative flex">
                <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-400" />
                <input value={cityQ} onChange={(e) => setCityQ(e.target.value)} disabled={!country}
                  placeholder={country ? "Type to search…" : "Pick a country first"}
                  className={`${sel} pl-9`} data-testid="explore-city-search" />
                <button onClick={() => setCityQ((v) => v.trim())} disabled={!country} data-testid="explore-city-search-go"
                  className="shrink-0 px-3 bg-[#1a73e8] text-white text-xs font-800 hover:opacity-90 disabled:opacity-40">Go&nbsp;&rsaquo;</button>
              </div>
            </div>
          </div>

          {country && (
            <div className="flex flex-wrap gap-2 mt-4 text-xs">
              {countryDoc && (
                <button onClick={() => openSlug(countryDoc.slug)} data-testid="explore-open-country"
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1a73e8] text-white font-800 hover:opacity-90">
                  <Compass className="w-4 h-4" /> Open {country} guide
                </button>
              )}
              {stateDoc && (
                <button onClick={() => openSlug(stateDoc.slug)} data-testid="explore-open-state"
                  className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#1a73e8] text-[#1a73e8] font-800 hover:bg-blue-50">
                  <Compass className="w-4 h-4" /> Open {state} guide
                </button>
              )}
            </div>
          )}
        </div>

        {country && (
          <div className="bg-white border border-zinc-300 p-5" data-testid="explore-city-results">
            <div className="font-heading font-900 text-lg mb-3 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#EA4335]" />
              {cityQ ? `Matches for "${cityQ}"` : state ? `Cities & towns in ${state}` : `Popular places in ${country}`}
              {loadingCities && <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />}
            </div>
            {cities !== null && cities.length === 0 && !loadingCities && (
              <p className="text-sm text-muted-foreground">No places found in our index — use the box below to open a guide for any town or village by name.</p>
            )}
            <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {(cities || []).map((c, i) => (
                <button key={c.slug} onClick={() => openSlug(c.slug)} data-testid={`explore-city-${c.slug.split("/").pop()}`}
                  style={{ background: chipTint(i) }}
                  className="text-left px-3 py-2 border border-zinc-200 text-sm font-600 text-zinc-800 hover:border-[#1a73e8] hover:text-[#1a73e8] transition-colors">
                  {c.name}
                  {c.state && <span className="block text-[10px] text-muted-foreground font-400">{c.state}</span>}
                </button>
              ))}
            </div>

            <div className="mt-6 border-t border-zinc-200 pt-4">
              <div className="text-sm font-800 flex items-center gap-1.5 mb-2">
                <Sparkles className="w-4 h-4 text-[#FBBC05]" /> Can't find it? Open a guide for any town or village:
              </div>
              <div className="flex gap-2 max-w-md">
                <input value={custom} onChange={(e) => setCustom(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && openCustom()}
                  placeholder="e.g. a small village name" className={sel} data-testid="explore-custom-place" />
                <button onClick={openCustom} disabled={!custom.trim()} data-testid="explore-custom-open"
                  className="px-4 py-2 bg-[#34A853] text-white text-sm font-800 hover:opacity-90 disabled:opacity-40 whitespace-nowrap">
                  Create guide
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">
                We'll generate a fresh AI travel guide for it in the background — usually ready in under a minute.
              </p>
            </div>
          </div>
        )}

        {popular && popular.length > 0 && (
          <div className="bg-white border border-zinc-300 p-5" data-testid="popular-destinations">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="font-heading font-900 text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#FBBC05]" /> {popular.length} Popular Destinations
                <Link to="/india-cities" data-testid="india-cities-link"
                  className="text-xs font-700 text-blue-700 hover:underline">India city directory →</Link>
              </div>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="border border-zinc-300 bg-white px-2 py-1.5 text-xs font-600"
                data-testid="popular-type-filter">
                <option value="">All types</option>
                {[...new Set(popular.map((p) => p.type))].sort().map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {popular.filter((p) => !typeFilter || p.type === typeFilter).map((p, i) => (
                <PopularChip key={p.slug} place={p} tint={chipTint(i)}
                  onOpen={() => nav(`/explore/${p.slug}`)} />
              ))}
            </div>
            <p className="mt-3 text-[10px] text-zinc-400">
              Hover a place to preview photos — courtesy of the photographers on{" "}
              <a href="https://unsplash.com/?utm_source=travelerpulse&utm_medium=referral" target="_blank"
                rel="noreferrer" className="underline">Unsplash</a>.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
